import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IUserInfo } from '../../../models/user';
import { filter, firstValueFrom, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../store/user/user.reducer';
import { ActivatedRoute, Router } from '@angular/router';
import { UserPaths } from '../../user/user-paths';
import { IGenerateOTPRequestBody } from '../../../models/authentication';
import { GenerateOTPAction, GenerateOTPFailureAction, GenerateOTPSuccessAction } from '../../../store/auth/auth.actions';
import { Actions, ofType } from '@ngrx/effects';
import { RefreshTokenAction, RefreshTokenFailureAction, RefreshTokenSuccessAction } from '../../../store/user/user.actions';
import { AuthService } from '../../../services/auth.service';
import { ParentalGateSessionService } from '../../../services/parentalGateSession.service';

@Component({
  selector: 'app-parental-gate',
  standalone: false,
  templateUrl: './parental-gate.component.html',
  styleUrl: './parental-gate.component.scss'
})
export class ParentalGateComponent implements OnInit, OnDestroy {
  isOtpSent: boolean = false;
  isSendingOtp = false;
  isVerifyingOtp = false;
  countdown: number = 120;
  timer: any;
  otpVerificationToken!: string;

  otpForm: FormGroup;
  userInfo: IUserInfo | null = null;
  userInfo$: Observable<IUserInfo | null>;
  private _subscriptions: Subscription[] = [];

  get otpControls(): string[] {
    return Object.keys(this.otpForm.controls);
  }

  get showOtpError(): boolean {
    const enteredOtp = Object.values(this.otpForm.value).join('');
    const regex = /^\d+$/;
    return !!enteredOtp && !regex.test(enteredOtp) && this.otpForm.invalid &&
      (this.otpForm.touched || this.otpForm.dirty);
  }

  constructor(private fb: FormBuilder, private store: Store, private route: ActivatedRoute,
    private router: Router, private actions$: Actions, private parentalGateSession: ParentalGateSessionService,
    private authService: AuthService,) {
    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.pattern('[0-9]*')]],
      otp2: ['', [Validators.required, Validators.pattern('[0-9]*')]],
      otp3: ['', [Validators.required, Validators.pattern('[0-9]*')]],
      otp4: ['', [Validators.required, Validators.pattern('[0-9]*')]],
      otp5: ['', [Validators.required, Validators.pattern('[0-9]*')]],
      otp6: ['', [Validators.required, Validators.pattern('[0-9]*')]]
    });

    this.userInfo$ = this.store.select(selectUserInfo);
  }

  ngOnInit(): void {
    this._subscriptions.push(
      this.userInfo$.pipe(filter(Boolean)).subscribe(async (userInfo) => {
        this.userInfo = await firstValueFrom(this.store.select(selectUserInfo).pipe(filter(Boolean)));
        return;
      })
    );

    // Generate OTP success
    const generateOTPSub = this.actions$.pipe(
      ofType(GenerateOTPSuccessAction),
    ).subscribe(({ response }) => {
      let otp = response.Value;
      if (otp) {
        this.isSendingOtp = false;
        this.isOtpSent = true;
        this.otpVerificationToken = otp.VerificationToken;
        this.startTimer(otp.ExpiresIn);
      }
    });
    this._subscriptions.push(generateOTPSub);

    // Generate OTP failure
    const generateOTPFailSub = this.actions$.pipe(
      ofType(GenerateOTPFailureAction),
    ).subscribe(({ error }) => {
      this.isSendingOtp = false;
      this.isOtpSent = false;
    });
    this._subscriptions.push(generateOTPFailSub);

    // Verify OTP success
    const otpVerificationSuccessSub = this.actions$.pipe(
      ofType(RefreshTokenSuccessAction),
    ).subscribe(({ response }) => {
      let returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || UserPaths.home;
      this.router.navigateByUrl(returnUrl);
      this.isVerifyingOtp = false;
      this.clearTimer();
      this.parentalGateSession.startSession();
    });
    this._subscriptions.push(otpVerificationSuccessSub);

    // Verify OTP Failure
    const otpVerificationFailSub = this.actions$.pipe(
      ofType(RefreshTokenFailureAction),
    ).subscribe(({ error }) => {
      this.isVerifyingOtp = false;
    });
    this._subscriptions.push(otpVerificationFailSub);
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  sendCode(): void {

    if (!this.userInfo?.Email) {
      return;
    }

    this.isSendingOtp = true;

    const otpInfo: IGenerateOTPRequestBody = {
      Receiver: this.userInfo.Email,
      DeliveryMode: 'EMAIL',
    }

    this.store.dispatch(GenerateOTPAction({
      request: otpInfo
    }))
  }

  cancel(): void {
    this.router.navigateByUrl(UserPaths.home);
  }

  startTimer(otpExpiresIn: number): void {
    this.countdown = otpExpiresIn;
    this.clearTimer();
    this.timer = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.clearTimer();
      }
    }, 1000);
  }

  clearTimer(): void {
    if (this.timer) clearInterval(this.timer);
  }

  resendOtp(): void {
    if (this.countdown === 0) {
      this.otpForm.reset();
      this.sendCode();
    }
  }

  onOtpInput(event: any, index: number): void {
    const input = event.target;
    const value = input.value;

    if (value && index < 5) {
      const nextInput = input.nextElementSibling as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && index > 0) {
      const currentInput = event.target as HTMLInputElement;
      if (!currentInput.value) {
        const prevInput = currentInput.previousElementSibling as HTMLInputElement;
        if (prevInput) prevInput.focus();
      }
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.verifyAndContinue();
    }
  }

  maskEmail(email: string | null | undefined) {
    if (!email || !email.includes('@')) {
      return email;
    }

    const [username, domain] = email.split('@');
    const lastDot = domain.lastIndexOf('.');

    if (lastDot === -1) {
      return email;
    }

    const domainName = domain.substring(0, lastDot);
    const extension = domain.substring(lastDot);

    const mask = (value: string): string => {
      if (value.length <= 2) {
        return value;
      }

      return (
        value.substring(0, 2) +
        'xxx' +
        value.substring(value.length - 2)
      );
    };

    return `${mask(username)}@${mask(domainName)}${extension}`;
  }

  verifyAndContinue(): void {

    this.otpForm.markAllAsTouched();

    if (this.otpForm.invalid || !this.userInfo?.Email) {
      return;
    }

    this.isVerifyingOtp = true;
    const enteredOtp = Object.values(this.otpForm.value).join('');

    // Refresh The Token with Parental OTP
    this.store.dispatch(RefreshTokenAction({
      request: {
        Token: this.authService.getTokenInformation()?.RefreshToken!,
        OtpUserInput: enteredOtp,
        OtpVerificationToken: this.otpVerificationToken,
        RefreshParentalControl: true
      }
    }));
    this.authService.tokenRefreshedByParentalGate();
  }
}
